import { SaveCartAsListButton } from "@/components/cart/SaveCartAsListButton";
import { OrderSummary } from "@/components/order-summary";
import {
  useFloatingButtonsLift,
  useHideFloatingButtons
} from "@/components/support/useFloatingButtonsLift";
import { formatPrice } from "@/utils/money";
import { Button } from "@medusajs/ui";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { cn } from "@repo/design-system/utils";
import { ChevronDown, ChevronUp } from "lucide-react";
import { memo, useState } from "react";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;
type StoreSellerCartsSummary = SalonStockerHttpTypes.StoreSellerCartsSummary;

export interface MobileCartSummaryBarProps {
  /** The seller carts being summarised. */
  carts: StoreSellerCart[];
  /** Aggregated totals across those carts. */
  summary: StoreSellerCartsSummary;
  /** Selected cart IDs (for the roll-up's "X sellers" line). */
  selectedCartIds: string[];
  /**
   * Proceed to the internal checkout. Omit to hide the button, e.g. when any
   * of the buyer's suppliers checks out on its own store and there is no
   * combined checkout to offer.
   */
  onCheckout?: () => void;
  /** Whether checkout is disabled (nothing selected). */
  isCheckoutDisabled?: boolean;
  /** Whether Save-as-list is disabled (empty cart). */
  saveDisabled?: boolean;
}

/**
 * The phone cart summary: a sticky bottom bar carrying the grand total and,
 * when `onCheckout` is given, the single Proceed-to-checkout action, so
 * checkout never scrolls away (KAN-73). Without `onCheckout` (an
 * external-checkout supplier rules the combined checkout out) the bar shows
 * only the total.
 * Tapping it expands the same OrderSummary roll-up in a sheet above the bar -
 * the per-supplier breakdown without losing your place. Per-supplier checkout
 * still lives on each seller card.
 *
 * CartPage mounts this only below `md` (via useBreakpoint); above that the
 * summary is an inline card (tablet) or the right-rail (desktop).
 */
export const MobileCartSummaryBar = memo(function MobileCartSummaryBar({
  carts,
  summary,
  selectedCartIds,
  onCheckout,
  isCheckoutDisabled = false,
  saveDisabled = false
}: MobileCartSummaryBarProps) {
  const [expanded, setExpanded] = useState(false);
  // The support FABs ride above this bar instead of over its right edge (while
  // the sheet is expanded they are hidden altogether, below).
  const barRef = useFloatingButtonsLift<HTMLDivElement>();
  // The scrim dims the page but not the `z-50` FABs, which would stay bright
  // above it and cover the sheet's content (the "Sent on" chip): hide them
  // while the sheet is open.
  useHideFloatingButtons(expanded);

  return (
    <>
      {/* Scrim behind the expanded sheet - tap anywhere off it to close. */}
      {expanded && (
        <button
          type="button"
          aria-label="Close order summary"
          className="fixed inset-0 z-40 bg-black/30"
          onClick={() => setExpanded(false)}
        />
      )}

      <div
        ref={barRef}
        className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.2)]"
      >
        {/* Expandable roll-up sheet - the same OrderSummary, minus its own
            checkout button since the bar below carries the action. */}
        {expanded && (
          <div className="max-h-[60vh] overflow-y-auto border-b border-gray-100 p-3">
            {/* A grabber-style close affordance at the top of the sheet: the bar
                below still toggles it, but from inside the open sheet a way out
                up here is far easier to find than the small chevron down at the
                bottom edge. */}
            <button
              type="button"
              onClick={() => setExpanded(false)}
              aria-label="Hide order summary"
              className="group -mt-1 mb-1 flex w-full flex-col items-center gap-0.5 rounded py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
            >
              <span className="h-1 w-9 rounded-full bg-gray-300 transition-colors group-hover:bg-gray-400" />
              <ChevronDown
                className="h-4 w-4 text-gray-400 transition-colors group-hover:text-gray-600"
                aria-hidden="true"
              />
            </button>

            <OrderSummary
              mode="cart"
              carts={carts}
              summary={summary}
              selectedCartIds={selectedCartIds}
              showContinueShopping={false}
            />
            <SaveCartAsListButton
              className="mt-3 w-full"
              disabled={saveDisabled}
            />
          </div>
        )}

        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
              aria-label={
                expanded ? "Hide order summary" : "Show order summary"
              }
              className="flex items-center gap-1.5 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
            >
              <ChevronUp
                className={cn(
                  "h-4 w-4 text-gray-500 transition-transform",
                  expanded && "rotate-180"
                )}
                aria-hidden="true"
              />
              <span>
                <span className="block text-xs text-gray-500">Total</span>
                <span className="block text-base font-bold text-gray-900">
                  {formatPrice(summary.grand_total)}
                </span>
              </span>
            </button>
          </div>

          {onCheckout && (
            <Button
              size="large"
              onClick={onCheckout}
              disabled={isCheckoutDisabled}
              className="whitespace-nowrap"
            >
              Proceed to checkout
            </Button>
          )}
        </div>
      </div>
    </>
  );
});
